import { apiClient } from '@/services/api/client'
import {
  clearDesktopConsoleBinding,
  getDesktopConsoleBinding,
  pollDesktopConsolePairing,
  startDesktopConsolePairing,
  type DesktopConsoleBindingSummary,
  type DesktopConsolePairingPollResult,
  type DesktopConsolePairingStart,
} from '@/integrations/tauri/consoleBinding'

export type ConsoleBindingSummary = DesktopConsoleBindingSummary
export type ConsolePairingStart = DesktopConsolePairingStart
export type ConsolePairingPollResult = DesktopConsolePairingPollResult

export type ConsoleBridgeSnapshot = {
  enabled?: boolean
  enrolled?: boolean
  last_enrollment_error?: string
  last_heartbeat_error?: string
  last_usage_report_error?: string
  last_catchup_error?: string
  last_deployment_report_error?: string
}

type ConsoleBindingStatusResponse = {
  binding: ConsoleBindingSummary | null
  bridge?: ConsoleBridgeSnapshot | null
  pairingSupported?: boolean
}

type ConsoleBindingState = {
  binding: ConsoleBindingSummary | null
  bridge: ConsoleBridgeSnapshot | null
  supported: boolean
}

const STATUS_CACHE_TTL_MS = 1500
let statusRequest: Promise<ConsoleBindingState> | null = null
let statusCache: {
  expiresAt: number
  value: ConsoleBindingState
} | null = null

function isUnavailableError(error: unknown) {
  return (
    error instanceof Error &&
    (error.message.includes('API Error 404:') ||
      error.message.includes('API Error 405:') ||
      error.message.includes('Desktop console binding is unavailable'))
  )
}

export async function getConsoleBindingState(): Promise<{
  binding: ConsoleBindingSummary | null
  bridge: ConsoleBridgeSnapshot | null
  supported: boolean
}> {
  if (statusCache && statusCache.expiresAt > Date.now()) {
    return statusCache.value
  }

  if (statusRequest) {
    return statusRequest
  }

  statusRequest = (async () => {
  try {
    const response =
      await apiClient.get<ConsoleBindingStatusResponse>('/standalone/console-binding')
    const value = {
      binding: response?.binding ?? null,
      bridge: response?.bridge ?? null,
      supported: response?.pairingSupported ?? true,
    }
    statusCache = {
      expiresAt: Date.now() + STATUS_CACHE_TTL_MS,
      value,
    }
    return value
  } catch (error) {
    if (!isUnavailableError(error)) {
      throw error
    }
  }

  const binding = await getDesktopConsoleBinding()
  const value = { binding, bridge: null, supported: binding != null }
  statusCache = {
    expiresAt: Date.now() + STATUS_CACHE_TTL_MS,
    value,
  }
  return value
  })().finally(() => {
    statusRequest = null
  })

  return statusRequest
}

export function hasStaleConsoleBinding(state: {
  binding: ConsoleBindingSummary | null
  bridge?: ConsoleBridgeSnapshot | null
}): boolean {
  if (!state.binding) {
    return false
  }

  const errors = [
    state.bridge?.last_enrollment_error,
    state.bridge?.last_heartbeat_error,
    state.bridge?.last_usage_report_error,
    state.bridge?.last_catchup_error,
    state.bridge?.last_deployment_report_error,
  ]

  return errors.some((error) =>
    error?.toLowerCase().includes('instance not found'),
  )
}

export async function startConsolePairing(input?: {
  consoleUrl?: string
  deviceName?: string
}): Promise<ConsolePairingStart> {
  try {
    return await apiClient.post<ConsolePairingStart>('/standalone/console-binding/authorize', {
      consoleUrl: input?.consoleUrl,
      deviceName: input?.deviceName,
    })
  } catch (error) {
    if (!isUnavailableError(error)) {
      throw error
    }
  }

  return startDesktopConsolePairing(input)
}

export async function pollConsolePairing(input: {
  consoleUrl?: string
  deviceCode: string
}): Promise<ConsolePairingPollResult> {
  try {
    return await apiClient.post<ConsolePairingPollResult>('/standalone/console-binding/token', {
      consoleUrl: input.consoleUrl,
      deviceCode: input.deviceCode,
    })
  } catch (error) {
    if (!isUnavailableError(error)) {
      throw error
    }
  }

  return pollDesktopConsolePairing(input)
}

export async function autoPairConsoleBinding(input?: {
  consoleUrl?: string
  deviceName?: string
}): Promise<ConsolePairingPollResult> {
  return apiClient.post<ConsolePairingPollResult>('/standalone/console-binding/auto', {
    consoleUrl: input?.consoleUrl,
    deviceName: input?.deviceName,
  })
}

export async function clearConsoleBinding() {
  try {
    await apiClient.delete('/standalone/console-binding')
    return
  } catch (error) {
    if (!isUnavailableError(error)) {
      throw error
    }
  }

  await clearDesktopConsoleBinding()
}
